import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  runTransaction,
  increment,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toDRXError } from '@/lib/drx-error';
import type { Review, ReviewStats } from '../reviews/review.types';

/**
 * Firestore-backed reviews and review votes (`reviews`, `reviewVotes`).
 */
export class ReviewRepository {
  private readonly reviewsCollection = 'reviews';
  private readonly reviewVotesCollection = 'reviewVotes';

  private get reviewsRef() {
    return collection(db, this.reviewsCollection);
  }

  private get reviewVotesRef() {
    return collection(db, this.reviewVotesCollection);
  }

  private toError(error: unknown, code: string, devMessage: string) {
    return toDRXError(error, { code, devMessage });
  }

  async createReview(reviewData: Omit<Review, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const existingReviewQuery = query(
        this.reviewsRef,
        where('userId', '==', reviewData.userId),
        where('targetId', '==', reviewData.targetId),
        where('targetType', '==', reviewData.targetType),
      );
      const existingReviews = await getDocs(existingReviewQuery);

      if (!existingReviews.empty) {
        throw new Error('You have already reviewed this item');
      }

      const data = { ...reviewData };
      if (data.targetType === 'course' || data.targetType === 'resource') {
        data.verified = await this.verifyPurchase(data.userId, data.targetId, data.targetType);
      }

      const docRef = await addDoc(this.reviewsRef, {
        ...data,
        helpful: 0,
        unhelpful: 0,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await this.updateTargetStats(data.targetId, data.targetType, data.rating, 'add');

      return docRef.id;
    } catch (error) {
      if (error instanceof Error && error.message === 'You have already reviewed this item') {
        throw error;
      }
      throw this.toError(error, 'REVIEW_CREATE_FAILED', 'Failed to create review');
    }
  }

  async updateReview(reviewId: string, updates: Partial<Review>): Promise<void> {
    try {
      const reviewRef = doc(this.reviewsRef, reviewId);
      const reviewDoc = await getDoc(reviewRef);

      if (!reviewDoc.exists()) {
        throw new Error('Review not found');
      }

      const oldRating = reviewDoc.data().rating as number;
      const newRating = updates.rating;

      await updateDoc(reviewRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      if (newRating !== undefined && newRating !== oldRating) {
        const reviewData = reviewDoc.data();
        await this.updateTargetStats(
          reviewData.targetId as string,
          reviewData.targetType as Review['targetType'],
          newRating,
          'update',
          oldRating,
        );
      }
    } catch (error) {
      if (error instanceof Error && error.message === 'Review not found') throw error;
      throw this.toError(error, 'REVIEW_UPDATE_FAILED', 'Failed to update review');
    }
  }

  async deleteReview(reviewId: string): Promise<void> {
    try {
      const reviewRef = doc(this.reviewsRef, reviewId);
      const reviewDoc = await getDoc(reviewRef);

      if (!reviewDoc.exists()) {
        throw new Error('Review not found');
      }

      const reviewData = reviewDoc.data();

      await deleteDoc(reviewRef);

      const votesQuery = query(this.reviewVotesRef, where('reviewId', '==', reviewId));
      const votes = await getDocs(votesQuery);
      await Promise.all(votes.docs.map((d) => deleteDoc(d.ref)));

      await this.updateTargetStats(
        reviewData.targetId as string,
        reviewData.targetType as Review['targetType'],
        reviewData.rating as number,
        'delete',
      );
    } catch (error) {
      if (error instanceof Error && error.message === 'Review not found') throw error;
      throw this.toError(error, 'REVIEW_DELETE_FAILED', 'Failed to delete review');
    }
  }

  async getTargetReviews(
    targetId: string,
    targetType: string,
    options?: {
      status?: string;
      limit?: number;
      orderBy?: 'newest' | 'oldest' | 'rating' | 'helpful';
    },
  ): Promise<Review[]> {
    try {
      let q = query(
        this.reviewsRef,
        where('targetId', '==', targetId),
        where('targetType', '==', targetType),
      );

      if (options?.status) {
        q = query(q, where('status', '==', options.status));
      }

      switch (options?.orderBy) {
        case 'oldest':
          q = query(q, orderBy('createdAt', 'asc'));
          break;
        case 'rating':
          q = query(q, orderBy('rating', 'desc'));
          break;
        case 'helpful':
          q = query(q, orderBy('helpful', 'desc'));
          break;
        case 'newest':
        default:
          q = query(q, orderBy('createdAt', 'desc'));
      }

      if (options?.limit) {
        q = query(q, limit(options.limit));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(
        (d) =>
          ({
            id: d.id,
            ...d.data(),
          }) as Review,
      );
    } catch (error) {
      throw this.toError(error, 'REVIEW_LIST_FAILED', 'Failed to list reviews for target');
    }
  }

  async getUserReviews(userId: string): Promise<Review[]> {
    try {
      const q = query(this.reviewsRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));

      const snapshot = await getDocs(q);
      return snapshot.docs.map(
        (d) =>
          ({
            id: d.id,
            ...d.data(),
          }) as Review,
      );
    } catch (error) {
      throw this.toError(error, 'REVIEW_USER_LIST_FAILED', 'Failed to list user reviews');
    }
  }

  async voteOnReview(reviewId: string, userId: string, voteType: 'helpful' | 'unhelpful'): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        const existingVoteQuery = query(
          this.reviewVotesRef,
          where('reviewId', '==', reviewId),
          where('userId', '==', userId),
        );
        const existingVotes = await getDocs(existingVoteQuery);

        const reviewRef = doc(this.reviewsRef, reviewId);

        if (!existingVotes.empty) {
          const existingVote = existingVotes.docs[0];
          const oldVoteType = existingVote.data().voteType as 'helpful' | 'unhelpful';

          if (oldVoteType === voteType) {
            transaction.delete(existingVote.ref);
            transaction.update(reviewRef, {
              [voteType]: increment(-1),
            });
          } else {
            transaction.update(existingVote.ref, {
              voteType,
              updatedAt: serverTimestamp(),
            });
            transaction.update(reviewRef, {
              [oldVoteType]: increment(-1),
              [voteType]: increment(1),
            });
          }
        } else {
          const voteRef = doc(this.reviewVotesRef);
          transaction.set(voteRef, {
            reviewId,
            userId,
            voteType,
            createdAt: serverTimestamp(),
          });
          transaction.update(reviewRef, {
            [voteType]: increment(1),
          });
        }
      });
    } catch (error) {
      throw this.toError(error, 'REVIEW_VOTE_FAILED', 'Failed to record review vote');
    }
  }

  async getTargetStats(targetId: string, targetType: string): Promise<ReviewStats> {
    try {
      const reviews = await this.getTargetReviews(targetId, targetType, { status: 'approved' });

      if (reviews.length === 0) {
        return {
          averageRating: 0,
          totalReviews: 0,
          ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
          recommendationRate: 0,
        };
      }

      const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      let totalRating = 0;
      let recommendedCount = 0;

      reviews.forEach((review) => {
        totalRating += review.rating;
        ratingDistribution[review.rating as keyof typeof ratingDistribution]++;
        if (review.recommended) recommendedCount++;
      });

      return {
        averageRating: totalRating / reviews.length,
        totalReviews: reviews.length,
        ratingDistribution,
        recommendationRate: (recommendedCount / reviews.length) * 100,
      };
    } catch (error) {
      throw this.toError(error, 'REVIEW_STATS_FAILED', 'Failed to compute review stats');
    }
  }

  async moderateReview(
    reviewId: string,
    status: 'approved' | 'rejected',
    adminNotes?: string,
  ): Promise<void> {
    try {
      const reviewRef = doc(this.reviewsRef, reviewId);
      await updateDoc(reviewRef, {
        status,
        adminNotes,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw this.toError(error, 'REVIEW_MODERATE_FAILED', 'Failed to moderate review');
    }
  }

  async flagReview(reviewId: string, reason: string): Promise<void> {
    try {
      const reviewRef = doc(this.reviewsRef, reviewId);
      await updateDoc(reviewRef, {
        status: 'flagged',
        flagReason: reason,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw this.toError(error, 'REVIEW_FLAG_FAILED', 'Failed to flag review');
    }
  }

  private async verifyPurchase(userId: string, targetId: string, targetType: string): Promise<boolean> {
    try {
      if (targetType === 'course') {
        const enrollmentsRef = collection(db, 'enrollments');
        const q = query(
          enrollmentsRef,
          where('userId', '==', userId),
          where('courseId', '==', targetId),
        );
        const snapshot = await getDocs(q);
        return !snapshot.empty;
      }
      if (targetType === 'resource') {
        const purchasesRef = collection(db, 'resourcePurchases');
        const q = query(
          purchasesRef,
          where('userId', '==', userId),
          where('resourceId', '==', targetId),
        );
        const snapshot = await getDocs(q);
        return !snapshot.empty;
      }

      return false;
    } catch (error) {
      console.error('Error verifying purchase:', error);
      return false;
    }
  }

  private async updateTargetStats(
    targetId: string,
    targetType: string,
    rating: number,
    operation: 'add' | 'update' | 'delete',
    oldRating?: number,
  ): Promise<void> {
    try {
      let collectionName: string;

      switch (targetType) {
        case 'course':
          collectionName = 'certificationCourses';
          break;
        case 'instructor':
          collectionName = 'users';
          break;
        case 'resource':
          collectionName = 'resources';
          break;
        case 'curriculum':
          collectionName = 'curriculum';
          break;
        default:
          return;
      }

      const targetRef = doc(db, collectionName, targetId);
      const targetDoc = await getDoc(targetRef);

      if (!targetDoc.exists()) return;

      const currentData = targetDoc.data();
      let reviewCount = (currentData.reviewCount as number) ?? 0;
      let reviewTotal = (currentData.reviewTotal as number) ?? 0;

      if (operation === 'add') {
        reviewCount++;
        reviewTotal += rating;
      } else if (operation === 'delete') {
        reviewCount = Math.max(0, reviewCount - 1);
        reviewTotal = Math.max(0, reviewTotal - rating);
      } else if (operation === 'update' && oldRating !== undefined) {
        reviewTotal = reviewTotal - oldRating + rating;
      }

      const averageRating = reviewCount > 0 ? reviewTotal / reviewCount : 0;

      await updateDoc(targetRef, {
        reviewCount,
        reviewTotal,
        averageRating,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating target stats:', error);
    }
  }
}
